import { GoogleGenAI, Type, Chat } from "@google/genai";
// FIX: Import GeneratedGltfModel type to be used by the new generate3dModel function.
import { Difficulty, ProjectIdea, Joint, FinishCalculation, WoodIdentification, ToolRecommendations, Budget, Currency, OptimizedCutList, CutListPart, LumberPiece, MeasurementSystem, GeneratedQuote, ClientDetails, DesignSuggestion, GeneratedGltfModel } from '../types.ts';

let ai: GoogleGenAI;

export const getAi = (): GoogleGenAI => {
    if (!ai) {
        const apiKey = process.env.API_KEY;
        if (!apiKey) {
            throw new Error("API Key not found. Please ensure the API_KEY environment variable is set.");
        }
        ai = new GoogleGenAI({ apiKey });
    }
    return ai;
}

// Schemas
const projectIdeasSchema = {
    type: Type.ARRAY,
    items: {
      type: Type.OBJECT,
      properties: {
        name: { type: Type.STRING, description: "Name of the woodworking project." },
        description: { type: Type.STRING, description: "A brief description of the project." },
        tools: { type: Type.ARRAY, items: { type: Type.STRING }, description: "List of required tools." },
        materials: { type: Type.ARRAY, items: { type: Type.STRING }, description: "List of required materials." },
      },
      required: ["name", "description", "tools", "materials"],
    },
};

const jointsLibrarySchema = {
    type: Type.ARRAY,
    items: {
        type: Type.OBJECT,
        properties: {
            name: { type: Type.STRING },
            description: { type: Type.STRING },
            uses: { type: Type.STRING },
            strength: { type: Type.STRING, description: "e.g., weak, moderate, strong" },
            svg_diagram: {
                type: Type.STRING,
                description: "A valid, 2D instructional line drawing as an SVG string. Use an exploded view to show how parts fit together. Must have a transparent background, viewBox='0 0 24 24', stroke='currentColor', stroke-width='1', and fill='none'. The diagram should be simple and clear for a beginner."
            }
        },
        required: ["name", "description", "uses", "strength", "svg_diagram"]
    }
};

const finishCalculatorSchema = {
    type: Type.OBJECT,
    properties: {
        ounces: { type: Type.NUMBER },
        gallons: { type: Type.NUMBER },
        recommendation: { type: Type.STRING }
    },
    required: ["ounces", "gallons", "recommendation"]
};

const woodIdentificationSchema = {
    type: Type.OBJECT,
    properties: {
        wood_species: { type: Type.STRING, description: "The most likely wood species." },
        characteristics: { type: Type.STRING, description: "Common characteristics like color, grain, hardness." },
        typical_uses: { type: Type.STRING, description: "Typical uses for this type of wood." },
        confidence: { type: Type.STRING, description: "Confidence level for the identification: High, Medium, or Low." },
        confidence_reasoning: { type: Type.STRING, description: "A brief reason for the stated confidence level." }
    },
    required: ["wood_species", "characteristics", "typical_uses", "confidence", "confidence_reasoning"]
};

const toolRecommendationsSchema = {
    type: Type.OBJECT,
    properties: {
        summary: { type: Type.STRING, description: "A brief summary of the tool philosophy for this budget and interest." },
        handTools: {
            type: Type.ARRAY,
            description: "List of essential hand tools.",
            items: {
                type: Type.OBJECT,
                properties: {
                    name: { type: Type.STRING, description: "Name of the hand tool." },
                    reason: { type: Type.STRING, description: "Brief reason why this tool is recommended for a beginner with the specified budget and interest." }
                },
                required: ["name", "reason"]
            }
        },
        powerTools: {
            type: Type.ARRAY,
            description: "List of essential power tools.",
            items: {
                type: Type.OBJECT,
                properties: {
                    name: { type: Type.STRING, description: "Name of the power tool." },
                    reason: { type: Type.STRING, description: "Brief reason why this tool is recommended for a beginner with the specified budget and interest." }
                },
                required: ["name", "reason"]
            }
        }
    },
    required: ["summary", "handTools", "powerTools"]
};

const cutListOptimizerSchema = {
    type: Type.OBJECT,
    properties: {
        summary: { type: Type.STRING, description: "A brief summary of the optimization results, mentioning overall waste or efficiency." },
        layouts: {
            type: Type.ARRAY,
            items: {
                type: Type.OBJECT,
                properties: {
                    lumberPieceIndex: { type: Type.INTEGER, description: "0-based index of the lumber piece from the user's input list this layout applies to." },
                    usedLumberDimensions: {
                        type: Type.OBJECT,
                        properties: {
                            width: { type: Type.NUMBER },
                            length: { type: Type.NUMBER }
                        },
                        required: ["width", "length"]
                    },
                    svg: { type: Type.STRING, description: "A valid SVG string representing the lumber board with the cut parts laid out. The viewBox must be '0 0 {length} {width}' of the board. Each cut part should be a <rect> with a <text> label inside showing its name and dimensions. Include a separate rectangle for the waste area. Use different colors for parts and waste. The SVG should be to scale." },
                    wastePercentage: { type: Type.NUMBER, description: "The percentage of this specific lumber piece that is waste." }
                },
                required: ["lumberPieceIndex", "svg", "wastePercentage", "usedLumberDimensions"]
            }
        },
        unfittableParts: {
            type: Type.ARRAY,
            items: {
                type: Type.OBJECT,
                properties: {
                    name: { type: Type.STRING },
                    quantity: { type: Type.INTEGER }
                },
                required: ["name", "quantity"]
            }
        }
    },
    required: ["summary", "layouts", "unfittableParts"]
};

const generateQuoteSchema = {
    type: Type.OBJECT,
    properties: {
        quoteNumber: { type: Type.STRING, description: "A unique quote number, e.g., Q-2024-001." },
        date: { type: Type.STRING, description: "The current date for the quote." },
        companyName: { type: Type.STRING },
        clientDetails: {
            type: Type.OBJECT,
            properties: {
                name: { type: Type.STRING },
                email: { type: Type.STRING },
                address: { type: Type.STRING }
            },
            required: ["name", "email", "address"]
        },
        costBreakdown: {
            type: Type.ARRAY,
            description: "An itemized list of all costs, including timber, labor, and additional items.",
            items: {
                type: Type.OBJECT,
                properties: {
                    item: { type: Type.STRING, description: "Description of the cost item (e.g., 'Oak Timber', 'Labor (8 hours)', 'Hinges')." },
                    cost: { type: Type.NUMBER }
                },
                required: ["item", "cost"]
            }
        },
        subtotal: { type: Type.NUMBER, description: "The sum of all items in the costBreakdown before markup and tax." },
        markupAmount: { type: Type.NUMBER, description: "The calculated markup amount based on the percentage provided." },
        taxAmount: { type: Type.NUMBER, description: "The calculated tax amount based on the percentage provided." },
        grandTotal: { type: Type.NUMBER, description: "The final total for the client (subtotal + markup + tax)." },
        notes: { type: Type.STRING, description: "Any additional notes for the client, such as payment terms or project timeline." },
        currency: { type: Type.STRING, enum: ["USD", "GBP", "EUR"], description: "The currency of the quote." }
    },
    required: ["quoteNumber", "date", "companyName", "clientDetails", "costBreakdown", "subtotal", "markupAmount", "taxAmount", "grandTotal", "notes", "currency"]
};

export const designSuggestionsSchema = {
    type: Type.ARRAY,
    description: "An array of 2 to 3 distinct furniture design suggestions.",
    items: {
        type: Type.OBJECT,
        properties: {
            designName: { type: Type.STRING, description: "A descriptive and appealing name for the design." },
            conceptOverview: { type: Type.STRING, description: "A brief, 2-3 sentence overview of the design's style and key features." },
            material: { type: Type.STRING, description: "The primary wood material for the project." },
            pieceList: {
                type: Type.ARRAY,
                description: "A detailed list of all individual wood components required for the project.",
                items: {
                    type: Type.OBJECT,
                    properties: {
                        pieceName: { type: Type.STRING, description: "The name of the component (e.g., Tabletop, Leg, Apron)." },
                        dimensions: { type: Type.STRING, description: "The precise dimensions, formatted as 'L\" x W\" x T\"' for imperial or 'Lmm x Wmm x Tmm' for metric." },
                        notes: { type: Type.STRING, description: "Optional notes on shaping, joinery, or other details for this specific piece. Can be an empty string." }
                    },
                    required: ["pieceName", "dimensions", "notes"]
                }
            }
        },
        required: ["designName", "conceptOverview", "material", "pieceList"]
    }
};

// API Functions
const callGemini = async (prompt: string, schema: object, systemInstruction?: string) => {
    const aiInstance = getAi();
    
    // The type definition for the config object is done this way to allow for the
    // optional inclusion of the systemInstruction property.
    const config: {
        responseMimeType: 'application/json';
        responseSchema: object;
        systemInstruction?: string;
    } = {
        responseMimeType: 'application/json',
        responseSchema: schema,
    };

    if (systemInstruction) {
        config.systemInstruction = systemInstruction;
    }

    const response = await aiInstance.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: config,
    });

    if (!response.text) {
        console.error("Gemini API returned an empty or undefined response.", response);
        const candidate = response.candidates?.[0];
        if (candidate?.finishReason === 'SAFETY') {
            throw new Error("The request was blocked due to safety concerns. Please modify your input.");
        }
        let errorMessage = "The AI model returned an empty response. This could be due to a content filter or an issue with the prompt.";
        if (candidate?.finishReason) {
            errorMessage += ` (Finish Reason: ${candidate.finishReason})`;
        }
        if (candidate?.finishMessage) {
            errorMessage += ` Message: ${candidate.finishMessage}`;
        }
        throw new Error(errorMessage);
    }

    return JSON.parse(response.text);
}

export const getProjectIdeas = async (difficulty: Difficulty): Promise<ProjectIdea[]> => {
    const systemInstruction = "You are a helpful assistant for woodworkers. Generate a list of project ideas based on the user's skill level.";
    const prompt = `Generate 5 woodworking project ideas for a ${difficulty} skill level.`;
    return callGemini(prompt, projectIdeasSchema, systemInstruction);
};

export const getJoints = async (): Promise<Joint[]> => {
    const systemInstruction = "You are a woodworking expert. Provide a list of common woodworking joints. For each joint, you must include its name, description, common uses, relative strength (weak, moderate, strong), and a simple, clear 2D SVG line drawing.";
    const prompt = `Provide a list of 5 common woodworking joints.`;
    return callGemini(prompt, jointsLibrarySchema, systemInstruction);
};

export const calculateFinish = async (area: number, coats: number, finishType: string): Promise<FinishCalculation> => {
    const systemInstruction = "You are a finish calculator for woodworkers. Based on the provided surface area, number of coats, and finish type, calculate the required amount of finish in both fluid ounces and gallons. Assume a standard coverage rate. Also provide a brief recommendation or tip for applying the specified finish.";
    const prompt = `Surface Area: ${area} sq. ft.\nNumber of Coats: ${coats}\nFinish Type: ${finishType}`;
    return callGemini(prompt, finishCalculatorSchema, systemInstruction);
};

export const identifyWood = async (base64Image: string, mimeType: string): Promise<WoodIdentification> => {
    const aiInstance = getAi();
    const systemInstruction = "You are a wood species identification expert. Analyze the provided image, focusing on grain pattern, color, and texture to determine the most likely wood species. Provide its characteristics, uses, and your confidence level with reasoning.";
    const prompt = "Identify the wood species from this image.";
    const imagePart = { inlineData: { data: base64Image, mimeType } };
    const textPart = { text: prompt };

    const response = await aiInstance.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: { parts: [imagePart, textPart] },
        config: {
            responseMimeType: 'application/json',
            responseSchema: woodIdentificationSchema,
            systemInstruction: systemInstruction,
        },
    });

    if (!response.text) {
        console.error("Gemini API returned an empty or undefined response for wood identification.", response);
        const candidate = response.candidates?.[0];
        if (candidate?.finishReason === 'SAFETY') {
            throw new Error("The request was blocked due to safety concerns. The image may have been flagged.");
        }
        let errorMessage = "The AI model returned an empty response. This could be due to a content filter or an issue with the prompt.";
        if (candidate?.finishReason) {
            errorMessage += ` (Finish Reason: ${candidate.finishReason})`;
        }
        if (candidate?.finishMessage) {
            errorMessage += ` Message: ${candidate.finishMessage}`;
        }
        throw new Error(errorMessage);
    }

    return JSON.parse(response.text);
};

export const visualizeStain = async (woodType: string, stainColor: string): Promise<string> => {
    const aiInstance = getAi();
    const prompt = `Generate a photorealistic, high-resolution image of a flat block of ${woodType} wood, stained with a ${stainColor} color. The image should be a close-up texture shot, showing the wood grain clearly.`;
    const response = await aiInstance.models.generateImages({
        model: 'imagen-4.0-generate-001',
        prompt: prompt,
        config: {
            numberOfImages: 1,
            outputMimeType: 'image/jpeg',
            aspectRatio: '1:1',
        },
    });
    return response.generatedImages[0].image.imageBytes;
};

export const getToolRecommendations = async (budget: Budget, interest: string, currency: Currency): Promise<ToolRecommendations> => {
    const systemInstruction = "You are an expert advisor for beginner woodworkers. Create a starter kit tool recommendation list based on a user's budget, primary interest, and currency. Provide a summary of your purchasing philosophy, a list of essential hand tools, and a list of essential power tools. For each tool, you must give a brief reason for the recommendation.";
    const prompt = `Budget: ${budget}\nPrimary Interest: ${interest}\nCurrency for tool pricing: ${currency}`;
    return callGemini(prompt, toolRecommendationsSchema, systemInstruction);
};

export const getOptimizedCutList = async (parts: CutListPart[], lumber: LumberPiece[], kerf: number, measurementSystem: MeasurementSystem): Promise<OptimizedCutList> => {
    const systemInstruction = `
        You are a cut list optimizer for a woodworker. Your goal is to create the most efficient layout of parts on available lumber to minimize waste.
        You will be given the parts to cut, the available lumber, and the saw blade kerf.
        Your response must follow these instructions:
        1. Arrange the parts on the lumber pieces to minimize waste. Account for the saw kerf in all calculations.
        2. Provide a layout for EACH piece of lumber used.
        3. For each layout, generate a to-scale SVG diagram. The SVG's viewBox must match the lumber dimensions.
        4. In the SVG, each part should be a <rect> with a fill color and a <text> label inside showing its name.
        5. Represent waste areas as separate <rect> elements with a different, lighter fill color.
        6. Calculate the waste percentage for each board.
        7. If any parts cannot fit on the available lumber, list them in the 'unfittableParts' array.
        8. Provide a brief summary of the results.`;
    const prompt = `
        Measurement System: ${measurementSystem}
        Saw Blade Kerf: ${kerf} ${measurementSystem === 'imperial' ? 'inches' : 'mm'}
        Parts to Cut (name, length, width, quantity):
        ${parts.map(p => `- ${p.name}: ${p.length} x ${p.width}, Qty: ${p.quantity}`).join('\n')}
        Available Lumber (length, width, quantity):
        ${lumber.map(l => `- ${l.length} x ${l.width}, Qty: ${l.quantity}`).join('\n')}
    `;
    return callGemini(prompt, cutListOptimizerSchema, systemInstruction);
};


export const generateQuote = async (
    companyName: string,
    clientDetails: ClientDetails,
    timberCost: number,
    laborHours: number,
    laborRate: number,
    markupPercent: number,
    taxPercent: number,
    additionalItems: Array<{description: string, cost: number}>,
    currency: Currency
): Promise<GeneratedQuote> => {
    const systemInstruction = `
        You are an automated quote generator for a woodworking business.
        Generate a professional quote based on the following details.
        The currency for this quote is ${currency}.
        Your instructions are:
        1. Create a unique quote number (e.g., Q-YYYY-NNN).
        2. Use today's date.
        3. Create an itemized list for the 'costBreakdown'. It should include separate lines for timber, total labor, and each additional item.
        4. Calculate the subtotal (sum of all costs).
        5. Calculate the markup amount.
        6. Calculate the tax amount.
        7. Calculate the grand total.
        8. Provide some generic notes for the client (e.g., "Quote valid for 30 days. 50% deposit required to begin work.").
        9. Ensure all monetary values are numbers and the final object matches the provided JSON schema.`;
    const prompt = `
        Company Name: ${companyName}
        Client: ${JSON.stringify(clientDetails)}
        Costs:
        - Timber: ${timberCost}
        - Labor: ${laborHours} hours at ${laborRate} per hour.
        - Additional Items: ${JSON.stringify(additionalItems)}
        Financials:
        - Markup: ${markupPercent}% (apply to the subtotal of timber, labor, and additional items).
        - Tax: ${taxPercent}% (apply to the subtotal after markup).
    `;
    return callGemini(prompt, generateQuoteSchema, systemInstruction);
};

export const generateDesignImage = async (description: string): Promise<string> => {
    const aiInstance = getAi();
    const prompt = `Generate a photorealistic, high-resolution image of a piece of furniture: ${description}. The furniture should be the main focus, displayed in a well-lit, professional studio setting with a clean, neutral background.`;
    const response = await aiInstance.models.generateImages({
        model: 'imagen-4.0-generate-001',
        prompt: prompt,
        config: {
            numberOfImages: 1,
            outputMimeType: 'image/jpeg',
            aspectRatio: '4:3',
        },
    });
    return response.generatedImages[0].image.imageBytes;
};

// FIX: Add stub for generate3dModel to resolve import error.
// This feature is not supported by the available APIs in the guidelines.
// Throws an error to indicate that it's not implemented.
export const generate3dModel = async (name: string, description: string): Promise<GeneratedGltfModel> => {
    throw new Error(`3D model generation for "${name}" (${description}) is not a supported feature.`);
};
